import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import {
  ACTION_POPUP_CONTENT_CLASS,
  ACTION_POPUP_CONTENT_INNER_CLASS,
} from '../../../components/popup/popupLayoutSpec';
import Heading from '../../../components/common/Typography/Heading';
import { disableScroll, enableScroll } from '../../../utils/scrollUtils';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import useRolePermissions from '../../../Hooks/useRolePermissions';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminHeaderAction,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import { buildSampleKeywords } from './keywordsDummyData';
import {
  EMPTY_TABLE_MESSAGE,
  KEYWORD_ROUTE,
  LIST_PREVIEW_COUNT,
  buildKeywordListGroups,
  formatKeywordListDate,
  keywordPreviewNames,
  keywordPreviewRows,
  subcategoryPreviewNames,
  subcategoryPreviewRows,
} from '../../../data/keywordWorkflow';

const PREVIEW_TABLE_COLS = 'minmax(92px, 0.45fr) minmax(0, 1fr)';

const PreviewNamesModal = ({ title, idLabel, nameLabel, rows, onClose }) => {
  useEffect(() => {
    disableScroll();
    return () => enableScroll();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/20 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="list-preview-modal-title"
    >
      <div className="flex-shrink-0 flex items-center justify-end h-[60px] sm:h-[80px] pr-4 sm:pr-6 z-10">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center text-offblack"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm cursor-pointer" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-white rounded-t-xl min-h-0">
        <div
          className="overflow-auto relative flex items-center justify-center"
          style={{ minHeight: 'calc(100vh - 120px)' }}
        >
          <div className={`${ACTION_POPUP_CONTENT_CLASS} pb-10`}>
            <div id="list-preview-modal-title">
              <Heading
                variant="h6"
                className="flex items-center justify-center text-center text-offblack"
              >
                {title}
              </Heading>
            </div>
            <div className={ACTION_POPUP_CONTENT_INNER_CLASS}>
              <div
                className="grid w-full py-3 text-offblack border-b border-lightgray gap-x-3"
                style={{ gridTemplateColumns: PREVIEW_TABLE_COLS }}
              >
                <div className="flex items-center min-w-0">
                  <AdminTableHeader className="truncate" title={idLabel}>
                    {idLabel}
                  </AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader className="truncate" title={nameLabel}>
                    {nameLabel}
                  </AdminTableHeader>
                </div>
              </div>

              {rows.map((row) => (
                <div
                  key={`${row.id}-${row.name}`}
                  className="grid w-full py-3 text-offblack border-b border-lightgray last:border-b-0 gap-x-3"
                  style={{ gridTemplateColumns: PREVIEW_TABLE_COLS }}
                >
                  <div className="flex items-center min-w-0">
                    <AdminTableBody className="truncate font-mono" title={row.id}>
                      {row.id}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableBody className="truncate" title={row.name}>
                      {row.name}
                    </AdminTableBody>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewListCell = ({ items, emptyLabel = '—', onViewAll }) => {
  const preview = (items || []).slice(0, LIST_PREVIEW_COUNT);
  const overflow = Math.max(0, (items || []).length - LIST_PREVIEW_COUNT);

  if (!items?.length) {
    return <AdminTableBody className="truncate block">{emptyLabel}</AdminTableBody>;
  }

  return (
    <AdminTableBody className="block min-w-0">
      <span className="truncate block" title={preview.join(', ')}>
        {preview.join(', ')}
        {overflow > 0 ? (
          <>
            {' '}
            <button
              type="button"
              className="text-primaryblue hover:underline whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                onViewAll?.();
              }}
            >
              +{overflow} more
            </button>
          </>
        ) : null}
      </span>
    </AdminTableBody>
  );
};

const tableCols =
  'minmax(96px, 0.7fr) minmax(140px, 1.15fr) minmax(140px, 1.15fr) minmax(140px, 1.15fr) minmax(110px, 0.85fr) minmax(110px, 0.85fr) minmax(48px, 0.22fr)';

const TABLE_MIN_WIDTH = '1080px';

const Keywords = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFullAccess, canPerformAction } = useRolePermissions();
  const canView = isFullAccess || canPerformAction('keywords_management', 'view');
  const canAdd = isFullAccess || canPerformAction('keywords_management', 'add');
  const canEdit = isFullAccess || canPerformAction('keywords_management', 'edit');

  const [keywords, setKeywords] = useState(() => buildSampleKeywords());
  const [saveSuccess, setSaveSuccess] = useState('');
  const [previewModal, setPreviewModal] = useState(null);

  const menuHeight =
    [canView, canAdd, canEdit].filter(Boolean).length >= 3
      ? ROW_ACTIONS_MENU_HEIGHT.TRIPLE
      : [canView, canAdd, canEdit].filter(Boolean).length === 2
        ? ROW_ACTIONS_MENU_HEIGHT.DOUBLE
        : ROW_ACTIONS_MENU_HEIGHT.SINGLE;

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(menuHeight);

  useEffect(() => {
    const incoming = location.state?.keywords;
    const flash = location.state?.flashMessage;
    if (Array.isArray(incoming)) {
      setKeywords(incoming);
    }
    if (flash) {
      setSaveSuccess(flash);
    }
    if (incoming || flash) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const displayedGroups = useMemo(() => buildKeywordListGroups(keywords), [keywords]);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRows,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(displayedGroups);

  const sharedState = { keywords };

  const openView = (row) => {
    navigate(`${KEYWORD_ROUTE}/${encodeURIComponent(row.tradeCategoryId)}/view`, {
      state: sharedState,
    });
  };

  const openAddKeywords = (row) => {
    navigate(`${KEYWORD_ROUTE}/add`, {
      state: {
        ...sharedState,
        tradeCategoryId: row.tradeCategoryId,
      },
    });
  };

  const openEdit = (row) => {
    navigate(`${KEYWORD_ROUTE}/${encodeURIComponent(row.tradeCategoryId)}/edit`, {
      state: sharedState,
    });
  };

  const openAddNew = () => {
    navigate(`${KEYWORD_ROUTE}/add`, { state: sharedState });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Keywords</AdminPageTitle>
          {canAdd ? (
            <AdminHeaderAction onClick={openAddNew}>Add New</AdminHeaderAction>
          ) : null}
        </div>

        {saveSuccess && (
          <AdminSupportingText className="text-freshgreen mb-4">{saveSuccess}</AdminSupportingText>
        )}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            {displayedGroups.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <AdminPanelTitle className="text-offblack">{EMPTY_TABLE_MESSAGE}</AdminPanelTitle>
              </div>
            ) : (
              <>
                <div
                  className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
                  style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                >
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader className="truncate">Trade Category</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0 pr-4">
                    <AdminTableHeader className="truncate">Pool Keywords</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0 pl-4">
                    <AdminTableHeader className="truncate">Subcategories</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0 pr-4">
                    <AdminTableHeader className="truncate">Subcategory Keywords</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0 pl-4">
                    <AdminTableHeader className="truncate">Created On</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader className="truncate">Last Updated On</AdminTableHeader>
                  </div>
                  <div className="flex items-center shrink-0 justify-center">
                    <AdminTableHeader>Actions</AdminTableHeader>
                  </div>
                </div>

                {paginatedRows.map((row) => {
                  const poolNames = keywordPreviewNames(row.poolKeywords);
                  const subcategoryNames = subcategoryPreviewNames(row.subcategories);
                  const keywordNames = keywordPreviewNames(row.keywords);

                  return (
                    <div
                      key={row.groupKey}
                      className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2"
                      style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                    >
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody
                          className="truncate block"
                          title={row.tradeCategoryName}
                        >
                          {row.tradeCategoryName || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden pr-4">
                        <PreviewListCell
                          items={poolNames}
                          emptyLabel="—"
                          onViewAll={() =>
                            setPreviewModal({
                              title: 'All Pool Keywords',
                              idLabel: 'Keyword ID',
                              nameLabel: 'Pool Keyword',
                              rows: keywordPreviewRows(row.poolKeywords),
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden pl-4">
                        <PreviewListCell
                          items={subcategoryNames}
                          emptyLabel="—"
                          onViewAll={() =>
                            setPreviewModal({
                              title: 'All Subcategories',
                              idLabel: 'Subcategory ID',
                              nameLabel: 'Subcategory',
                              rows: subcategoryPreviewRows(row.subcategories),
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden pr-4">
                        <PreviewListCell
                          items={keywordNames}
                          emptyLabel="—"
                          onViewAll={() =>
                            setPreviewModal({
                              title: 'All Subcategory Keywords',
                              idLabel: 'Keyword ID',
                              nameLabel: 'Subcategory Keyword',
                              rows: keywordPreviewRows(row.keywords),
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden pl-4">
                        <AdminTableBody className="truncate block" title={row.createdOn}>
                          {formatKeywordListDate(row.createdOn)}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={row.lastUpdatedOn}>
                          {formatKeywordListDate(row.lastUpdatedOn)}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center justify-center shrink-0 overflow-visible">
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[row.groupKey] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={() => {
                              toggleRowActions(row.groupKey);
                            }}
                            aria-expanded={!!rowActionsOpen[row.groupKey]}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[row.groupKey] && rowActionsMenuLayout && (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              {canView ? (
                                <AdminRowMenuItem
                                  onClick={() => {
                                    closeRowActions();
                                    openView(row);
                                  }}
                                >
                                  View
                                </AdminRowMenuItem>
                              ) : null}
                              {canAdd ? (
                                <AdminRowMenuItem
                                  onClick={() => {
                                    closeRowActions();
                                    openAddKeywords(row);
                                  }}
                                >
                                  Add Keywords
                                </AdminRowMenuItem>
                              ) : null}
                              {canEdit ? (
                                <AdminRowMenuItem
                                  onClick={() => {
                                    closeRowActions();
                                    openEdit(row);
                                  }}
                                >
                                  Edit
                                </AdminRowMenuItem>
                              ) : null}
                            </AdminFixedRowActionsMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>

      {previewModal ? (
        <PreviewNamesModal
          title={previewModal.title}
          idLabel={previewModal.idLabel}
          nameLabel={previewModal.nameLabel}
          rows={previewModal.rows}
          onClose={() => setPreviewModal(null)}
        />
      ) : null}
    </AdminLayout>
  );
};

export default Keywords;
